// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/** Small pure formatting helpers used across the UI. */

/** Human-readable byte size, e.g. 1536 → "1.5 KB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exp);
  const digits = exp === 0 ? 0 : value < 10 ? 1 : value < 100 ? 1 : 0;
  return `${value.toFixed(digits)} ${units[exp]}`;
}

/**
 * Compression ratio as a saved-percentage string.
 * compressed=300, original=1000 → "70% smaller".
 */
export function formatRatio(original: number, compressed: number): string {
  if (original <= 0) return '—';
  const saved = 1 - compressed / original;
  if (saved <= 0) return '0%';
  return `${Math.round(saved * 100)}%`;
}

/** Throughput from bytes processed over elapsed milliseconds. */
export function formatThroughput(bytes: number, ms: number): string {
  if (ms <= 0 || bytes <= 0) return '—';
  const perSec = bytes / (ms / 1000);
  return `${formatBytes(perSec)}/s`;
}

/**
 * Turn an arbitrary string into a safe download filename (used for the
 * generated archive name). Keeps a sensible default if nothing usable remains.
 */
export function safeDownloadName(name: string, fallback: string): string {
  const base = name.replace(/[/\\:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();
  return base || fallback;
}

/** Derive a default archive name when none is implied by the inputs. */
export function defaultArchiveName(files: { name: string }[]): string {
  if (files.length === 1) {
    const dot = files[0].name.lastIndexOf('.');
    const stem = dot > 0 ? files[0].name.slice(0, dot) : files[0].name;
    return `${stem || 'archive'}.zip`;
  }
  return 'archive.zip';
}
